import type { GridCell, GridData, Rack, RouteResult } from '../models/types';

const CELL_COLORS: Record<string, { dark: string; light: string }> = {
  dock: { dark: '#e07a5f', light: '#d45d3a' },
  wall: { dark: '#1e1e2e', light: '#999' },
  aisle: { dark: '#2e3d4f', light: '#c8d8e4' },
  rack: { dark: '#4a7ab5', light: '#5a8ac5' },
  rack_stock: { dark: '#b5854a', light: '#c59555' },
  cross_aisle: { dark: '#3a5a3a', light: '#8cb88c' },
};

const LOCAL_RACK_COLOR = { dark: '#d4a017', light: '#e6b422' };

const FLOOR_COLOR = { dark: '#0e0e1a', light: '#eee' };

export interface RenderState {
  grid: GridData;
  racksById: Map<number, Rack>;
  racksByPos: Map<string, Rack>;
  route: RouteResult | null;
  selectedCells: Set<string>;
  deletedRackIds?: Set<number>;
  localRackIds?: number[];
  localWallCells?: Set<string>;
  scale: number;
  panX: number;
  panY: number;
  hoveredCell: GridCell | null;
  theme: 'dark' | 'light';
}

function cellToScreen(
  cellX: number,
  cellY: number,
  cols: number,
  rows: number,
  scale: number,
): { sx: number; sy: number } {
  return {
    sx: (cellX - cols / 2) * scale,
    sy: (rows / 2 - 1 - cellY) * scale,
  };
}

export function screenToCell(
  clientX: number,
  clientY: number,
  canvasWidth: number,
  canvasHeight: number,
  cols: number,
  rows: number,
  scale: number,
  panX: number,
  panY: number,
): { gx: number; gy: number } {
  const wx = (clientX - canvasWidth / 2 - panX) / scale;
  const wy = (clientY - canvasHeight / 2 - panY) / scale;
  return {
    gx: Math.floor(wx + cols / 2),
    gy: Math.floor(rows / 2 - wy),
  };
}

function rackHasStock(racksByPos: Map<string, Rack>, cellY: number, cellX: number): boolean {
  const rack = racksByPos.get(`${cellY}:${cellX}`);
  if (!rack?.locations) return false;
  return rack.locations.some((loc) => (loc.stock_location?.stock ?? 0) > 0);
}

export function render(ctx: CanvasRenderingContext2D, width: number, height: number, state: RenderState): void {
  const {
    grid,
    racksByPos,
    route,
    selectedCells,
    deletedRackIds,
    localRackIds,
    localWallCells,
    scale,
    panX,
    panY,
    hoveredCell,
    theme,
  } = state;

  ctx.clearRect(0, 0, width, height);
  ctx.fillStyle = FLOOR_COLOR[theme];
  ctx.fillRect(0, 0, width, height);

  if (!grid) return;

  const { grid_cols: cols, grid_rows: rows } = grid;
  const gap = 2;
  const s = scale - gap;

  ctx.save();
  ctx.translate(width / 2 + panX, height / 2 + panY);

  drawCells(
    ctx,
    grid,
    racksByPos,
    selectedCells,
    deletedRackIds,
    localRackIds,
    localWallCells,
    cols,
    rows,
    scale,
    s,
    hoveredCell,
    theme,
  );
  drawWalkableDots(ctx, grid, cols, rows, s);
  drawAxisLabels(ctx, cols, rows, scale, s);

  if (route?.paths?.length) {
    drawRoutePaths(ctx, route.paths, cols, rows, scale, s);
  }
  if (route?.stops) {
    drawStopNumbers(ctx, route, cols, rows, scale, s);
  }

  ctx.restore();
}

function drawCells(
  ctx: CanvasRenderingContext2D,
  grid: GridData,
  racksByPos: Map<string, Rack>,
  selectedCells: Set<string>,
  deletedRackIds: Set<number> | undefined,
  localRackIds: number[] | undefined,
  localWallCells: Set<string> | undefined,
  cols: number,
  rows: number,
  scale: number,
  s: number,
  hoveredCell: GridCell | null,
  theme: 'dark' | 'light',
): void {
  for (const cell of grid.cells) {
    const { sx, sy } = cellToScreen(cell.x, cell.y, cols, rows, scale);

    const rackAtCell = racksByPos.get(`${cell.y}:${cell.x}`);
    const isLocalRack = localRackIds?.includes(rackAtCell?.id ?? -999) ?? false;
    const hasStock = !isLocalRack && rackHasStock(racksByPos, cell.y, cell.x);

    let color: { dark: string; light: string };
    if (isLocalRack) {
      color = LOCAL_RACK_COLOR;
    } else if (hasStock) {
      color = CELL_COLORS.rack_stock;
    } else if (rackAtCell) {
      color = CELL_COLORS.rack;
    } else {
      color = CELL_COLORS[cell.type] || CELL_COLORS.aisle;
    }

    ctx.fillStyle = color[theme] || color.dark;

    if ((cell.type === 'rack' || rackAtCell) && selectedCells.has(`${cell.y}:${cell.x}`)) {
      ctx.fillStyle = 'rgba(180,80,220,0.7)';
    }

    if (hoveredCell && hoveredCell.x === cell.x && hoveredCell.y === cell.y) {
      const isRackCell = cell.type === 'rack' || rackAtCell;
      if (isRackCell) {
        const rackId = cell.rack_id ?? rackAtCell?.id ?? -1;
        const isDeleted = deletedRackIds?.has(rackId) ?? false;
        if (!isDeleted) {
          ctx.fillStyle = 'rgba(180,80,220,0.5)';
        }
      } else {
        ctx.fillStyle = '#ffcc00';
      }
    }

    ctx.fillRect(sx, sy, s, s);

    // Local wall cell indicator (dashed border)
    const isLocalWall = localWallCells?.has(`${cell.y}:${cell.x}`) ?? false;
    if (isLocalWall && cell.type !== 'wall') {
      ctx.strokeStyle = '#f59e0b';
      ctx.lineWidth = 2;
      ctx.setLineDash([4, 4]);
      ctx.strokeRect(sx + 1, sy + 1, s - 2, s - 2);
      ctx.setLineDash([]);
    }

    // Aisle direction hint
    const hasRackHere = cell.type === 'rack' || rackAtCell;
    if (!hasRackHere && cell.type === 'aisle') {
      ctx.strokeStyle = 'rgba(255,255,255,0.08)';
      ctx.lineWidth = 1;
      const step = s / 4;
      for (let i = 1; i < 4; i++) {
        ctx.beginPath();
        ctx.moveTo(sx, sy + i * step);
        ctx.lineTo(sx + s, sy + i * step);
        ctx.stroke();
      }
    }

    // Rack label
    if (cell.type === 'rack' && cell.rack_id) {
      const rack = racksByPos.get(`${cell.y}:${cell.x}`);
      const label = rack ? rack.code : `#${cell.rack_id}`;
      ctx.fillStyle = '#fff';
      ctx.font = `bold ${Math.max(10, scale * 0.25)}px monospace`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(label, sx + s / 2, sy + s / 2);

      // Delete indicator (X mark)
      const isDeleted = deletedRackIds?.has(cell.rack_id) ?? false;
      if (isDeleted) {
        const xSize = Math.max(6, scale * 0.15);
        ctx.strokeStyle = '#ff3b3b';
        ctx.lineWidth = 2;
        // Draw X
        ctx.beginPath();
        ctx.moveTo(sx + s / 2 - xSize, sy + s / 2 - xSize);
        ctx.lineTo(sx + s / 2 + xSize, sy + s / 2 + xSize);
        ctx.stroke();
        ctx.beginPath();
        ctx.moveTo(sx + s / 2 + xSize, sy + s / 2 - xSize);
        ctx.lineTo(sx + s / 2 - xSize, sy + s / 2 + xSize);
        ctx.stroke();
      }

      if (hasStock) {
        const r = Math.max(3, scale * 0.08);
        ctx.beginPath();
        ctx.arc(sx + s - r - 2, sy + r + 2, r, 0, Math.PI * 2);
        ctx.fillStyle = '#ff9933';
        ctx.fill();
      }
    }

    if (!cell.type.includes('rack') && rackAtCell) {
      const label = rackAtCell.code;
      ctx.fillStyle = '#fff';
      ctx.font = `bold ${Math.max(10, scale * 0.25)}px monospace`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(label, sx + s / 2, sy + s / 2);

      // Delete indicator (X mark)
      const isDeleted = deletedRackIds?.has(rackAtCell.id) ?? false;
      if (isDeleted) {
        const xSize = Math.max(6, scale * 0.15);
        ctx.strokeStyle = '#ff3b3b';
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.moveTo(sx + s / 2 - xSize, sy + s / 2 - xSize);
        ctx.lineTo(sx + s / 2 + xSize, sy + s / 2 + xSize);
        ctx.stroke();
        ctx.beginPath();
        ctx.moveTo(sx + s / 2 + xSize, sy + s / 2 - xSize);
        ctx.lineTo(sx + s / 2 - xSize, sy + s / 2 + xSize);
        ctx.stroke();
      }

      if (hasStock) {
        const r = Math.max(3, scale * 0.08);
        ctx.beginPath();
        ctx.arc(sx + s - r - 2, sy + r + 2, r, 0, Math.PI * 2);
        ctx.fillStyle = '#ff9933';
        ctx.fill();
      }
    }

    // Dock label
    if (cell.type === 'dock' && !rackAtCell) {
      ctx.fillStyle = '#fff';
      ctx.font = `bold ${Math.max(10, scale * 0.22)}px monospace`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText('DOCK', sx + s / 2, sy + s / 2);
    }
  }
}

function drawWalkableDots(ctx: CanvasRenderingContext2D, grid: GridData, cols: number, rows: number, s: number): void {
  const scale = s + 2; // restore original scale for positioning
  for (const cell of grid.cells) {
    if (!cell.walkable || cell.type === 'dock') continue;
    const { sx, sy } = cellToScreen(cell.x, cell.y, cols, rows, scale);
    ctx.beginPath();
    ctx.arc(sx + s / 2, sy + s / 2, 2, 0, Math.PI * 2);
    ctx.fillStyle = 'rgba(255,255,255,0.25)';
    ctx.fill();
  }
}

function drawAxisLabels(ctx: CanvasRenderingContext2D, cols: number, rows: number, scale: number, s: number): void {
  ctx.fillStyle = '#888';
  ctx.font = `${Math.max(8, scale * 0.18)}px monospace`;

  // X labels (bottom)
  ctx.textAlign = 'center';
  ctx.textBaseline = 'top';
  for (let gx = 0; gx < cols; gx++) {
    const { sx, sy } = cellToScreen(gx, 0, cols, rows, scale);
    ctx.fillText(`${gx}`, sx + s / 2, sy + s + 4);
  }

  // Y labels (left)
  ctx.textAlign = 'right';
  ctx.textBaseline = 'middle';
  for (let gy = 0; gy < rows; gy++) {
    const { sx, sy } = cellToScreen(0, gy, cols, rows, scale);
    ctx.fillText(`${gy}`, sx - 6, sy + s / 2);
  }
}

function drawRoutePaths(
  ctx: CanvasRenderingContext2D,
  paths: PathSegment[],
  cols: number,
  rows: number,
  scale: number,
  s: number,
): void {
  ctx.lineWidth = 3;
  ctx.setLineDash([6, 4]);

  for (const seg of paths) {
    if (seg.cells.length < 2) continue;
    ctx.strokeStyle = seg.type === 'return' ? '#33ccff' : '#ff9933';
    ctx.beginPath();
    const p0 = cellToScreen(seg.cells[0].x, seg.cells[0].y, cols, rows, scale);
    ctx.moveTo(p0.sx + s / 2, p0.sy + s / 2);
    for (let j = 1; j < seg.cells.length; j++) {
      const pj = cellToScreen(seg.cells[j].x, seg.cells[j].y, cols, rows, scale);
      ctx.lineTo(pj.sx + s / 2, pj.sy + s / 2);
    }
    ctx.stroke();
  }

  ctx.setLineDash([]);
}

function drawStopNumbers(
  ctx: CanvasRenderingContext2D,
  route: RouteResult,
  cols: number,
  rows: number,
  scale: number,
  s: number,
): void {
  const seen = new Set<string>();
  let stopNum = 0;

  for (const stop of route.stops) {
    const key = `${stop.rack_row}:${stop.rack_col}`;
    if (seen.has(key)) continue;
    seen.add(key);
    stopNum++;

    const { sx, sy } = cellToScreen(stop.rack_col, stop.rack_row, cols, rows, scale);
    const r = Math.max(10, scale * 0.2);

    // Circle badge
    ctx.beginPath();
    ctx.arc(sx + s - r - 1, sy + r + 1, r, 0, Math.PI * 2);
    ctx.fillStyle = '#ff9933';
    ctx.fill();
    ctx.strokeStyle = '#000';
    ctx.lineWidth = 1;
    ctx.stroke();

    // Number
    ctx.fillStyle = '#000';
    ctx.font = `bold ${Math.max(9, r)}px monospace`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(`${stopNum}`, sx + s - r - 1, sy + r + 2);
  }
}
